import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { findCoding, formatCodingToken } from '@lifeomic/fhir-constants';
import { unwrapBundle } from '@lifeomic/phc-web-toolkit/src/util/fhir';
import type { WithPlatformDefaults } from '@lifeomic/service-clients/fhir-client';
import { fhirClient, useDataset } from '../../../../util/api';
import { fhirSystems } from '../../OnboardingSnapshot/fhir-systems';
import { UPCOMING_APPOINTMENT_STATUSES } from '../../OnboardingSnapshot/dataSources';
import { usePractitionerNames } from '../../../../util/hooks/usePractitionerNames';

export type NextAppointmentSummary = {
  start?: string;
  type?: string;
  practitionerName?: string;
  locationType?: string;
  // True when there is no upcoming appointment and we are instead surfacing the
  // member's most recent past appointment.
  isPast?: boolean;
};

// Statuses that represent an appointment that actually took place, used when
// falling back to the member's most recent previous appointment.
const PAST_APPOINTMENT_STATUSES: Array<fhir3.Appointment['status']> = [
  'fulfilled',
  'arrived',
  'booked',
];

type AppointmentResult = {
  appointment: WithPlatformDefaults<fhir3.Appointment>;
  isPast: boolean;
};

const getPractitionerParticipantId = (
  appointment: WithPlatformDefaults<fhir3.Appointment>,
): string | null => {
  const practitionerParticipant = appointment.participant?.find(participant =>
    participant.actor?.reference?.startsWith('Practitioner/'),
  );
  const reference = practitionerParticipant?.actor?.reference;
  return reference ? reference.slice('Practitioner/'.length) || null : null;
};

/**
 * Fetches the soonest upcoming (future) appointment for a member directly from
 * FHIR and resolves the practitioner display name. Unlike the priorities detail
 * endpoint (which only returns appointments scheduled for today), this surfaces
 * the next appointment regardless of how far out it is scheduled.
 *
 * When the member has no upcoming appointment, this falls back to their most
 * recent past appointment instead (flagged via `isPast`).
 */
export const useNextAppointment = (
  patientId: string,
): { appointment: NextAppointmentSummary | null; isLoading: boolean } => {
  const dataset = useDataset();

  const query = useQuery({
    queryKey: ['priorities-next-appointment', patientId, dataset?.codingToken],
    queryFn: async (): Promise<AppointmentResult | null> => {
      const now = new Date().toISOString();

      const fetchAppointments = async (
        date: string,
        sort: 'date' | '-date',
      ): Promise<Array<WithPlatformDefaults<fhir3.Appointment>>> => {
        const response = await fhirClient.request(
          'GET /v1/fhir/dstu3/Appointment',
          {
            _tag: [
              dataset.codingToken,
              formatCodingToken({
                system: fhirSystems.source.system,
                code: fhirSystems.source.values.elationAppointment,
              }),
            ],
            patient: patientId,
            _sort: sort,
            date,
            pageSize: 25,
          },
        );

        return unwrapBundle(response.data) as Array<
          WithPlatformDefaults<fhir3.Appointment>
        >;
      };

      const upcoming = (await fetchAppointments(`ge${now}`, 'date'))
        .filter(appointment =>
          UPCOMING_APPOINTMENT_STATUSES.includes(appointment.status),
        )
        .sort(
          (a, b) =>
            new Date(a.start ?? 0).getTime() - new Date(b.start ?? 0).getTime(),
        )[0];

      if (upcoming) {
        return { appointment: upcoming, isPast: false };
      }

      const previous = (await fetchAppointments(`lt${now}`, '-date'))
        .filter(appointment =>
          PAST_APPOINTMENT_STATUSES.includes(appointment.status),
        )
        .sort(
          (a, b) =>
            new Date(b.start ?? 0).getTime() - new Date(a.start ?? 0).getTime(),
        )[0];

      return previous ? { appointment: previous, isPast: true } : null;
    },
    enabled: Boolean(patientId) && Boolean(dataset?.codingToken),
  });

  const result = query.data ?? null;
  const raw = result?.appointment ?? null;
  const practitionerId = raw ? getPractitionerParticipantId(raw) : null;
  const practitionerNames = usePractitionerNames(
    practitionerId ? [practitionerId] : [],
  );

  const appointment = useMemo<NextAppointmentSummary | null>(() => {
    if (!raw) {
      return null;
    }
    return {
      start: raw.start,
      type:
        findCoding(raw.meta?.tag, {
          system: fhirSystems.appointment.elationReason.system,
        })?.code ||
        raw.description ||
        undefined,
      practitionerName: practitionerId
        ? practitionerNames[practitionerId]
        : undefined,
      locationType: findCoding(raw.meta?.tag, {
        system: fhirSystems.appointment.locationType.system,
      })?.code,
      isPast: result?.isPast ?? false,
    };
  }, [raw, result?.isPast, practitionerId, practitionerNames]);

  return { appointment, isLoading: query.isLoading };
};
